import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionContext.jsx';
import Footer from '../components/Footer.jsx';

// Code 6 caractères alphanumériques, sans séparateur.
function formatCode(raw) {
  return raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
}

export default function Login() {
  const { login, session, loading } = useSession();
  const nav = useNavigate();
  const inputRef = useRef(null);

  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // If already logged in, bounce to home.
  useEffect(() => {
    if (!loading && session) nav('/', { replace: true });
  }, [loading, session, nav]);

  // Auto-focus on mount.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const onChange = (e) => {
    setValue(formatCode(e.target.value));
    if (error) setError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const code = value.trim();
    if (!code) return;
    setSubmitting(true);
    setError('');
    try {
      await login(code);
      nav('/', { replace: true });
    } catch (err) {
      if (err.status === 401) {
        setError('Code invalide ou expiré');
      } else if (err.status === 429) {
        setError('Trop de tentatives, réessayez dans quelques minutes');
      } else {
        setError('Connexion impossible. Réessayez.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={onSubmit}>
        <img
          src="/logo.png"
          alt="Vertuoso"
          className="login__logo"
          onError={(e) => { e.currentTarget.src = '/logo-placeholder.svg'; }}
        />
        <h1 className="login__title">Connexion</h1>

        <label htmlFor="code" className="login__label">Code d'accès</label>
        <input
          id="code"
          ref={inputRef}
          className="vrt-input login__input"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
          placeholder="K7M2X9"
          maxLength={6}
          value={value}
          onChange={onChange}
          disabled={submitting}
          aria-invalid={!!error}
          aria-describedby={error ? 'code-error' : undefined}
        />

        {error && (
          <div id="code-error" className="login__error" role="alert">{error}</div>
        )}

        <button
          type="submit"
          className="vrt-btn login__submit"
          disabled={submitting || value.length === 0}
        >
          {submitting ? 'Connexion…' : 'Entrer'}
        </button>
      </form>

      <Footer />

      <style>{`
        .login {
          min-height: 100vh;
          background: #fff;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .login__card {
          width: 100%;
          max-width: 380px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .login__logo {
          align-self: center;
          height: 80px;
          width: auto;
          max-width: 100%;
          object-fit: contain;
          margin-bottom: 8px;
          display: block;
        }
        .login__title {
          margin: 0 0 4px;
          text-align: center;
          font-size: 22px;
          color: var(--vrt-navy);
          font-weight: 700;
        }
        .login__label {
          font-size: 14px;
          color: var(--vrt-text-muted);
          margin-bottom: -6px;
        }
        .login__input {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          letter-spacing: 0.3em;
          font-size: 28px;
          text-align: center;
          font-weight: 700;
          min-height: 56px;
        }
        .login__error {
          color: var(--vrt-danger);
          font-size: 14px;
          background: #fdecea;
          border: 1px solid #f5c6c2;
          border-radius: var(--vrt-radius);
          padding: 10px 12px;
        }
        .login__submit {
          width: 100%;
          margin-top: 4px;
          min-height: 56px;
        }
        @media (max-width: 480px) {
          .login {
            padding: 16px 8px;
          }
          .login__card {
            width: 100%;
            padding: 0 4px;
            max-width: 100%;
          }
          .login__logo {
            height: 56px;
            max-width: 80%;
          }
          .login__input {
            font-size: 24px;
            letter-spacing: 0.2em;
          }
        }
      `}</style>
    </div>
  );
}
